import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { RestrictedLayout } from "src/components/layouts";
import {
    TrialInstitutionPersonnelCreateBreadcrumbs,
    TrialInstitutionPersonnelDetailsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { TrialInstitutionPersonnelSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { NotificationAlert } from "../../../components/common/index";
import { DatePickerFormInputFabric, SelectFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./TrialInstitutionPersonnelPage.scss";

interface TrialInstitutionPersonnelPageProps {
    form: Dtos.TrialPersonnel & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialPersonnel: Dtos.TrialPersonnel;
    trialInstitution: Dtos.TrialInstitution;
    lookups: Dtos.Lookup[];
    loadingTrialInstitution: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    savingTrialPersonnel: boolean;
    saveTrialPersonnelSuccess: boolean;
    saveTrialPersonnelFailure: boolean;
    creatingTrialPersonnel: boolean;
    validatingTrialPersonnel: boolean;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelLoadByIdActionCreator;
    saveTrialPersonnel: TrialPersonnelActions.ITrialPersonnelSaveActionCreator;
    createTrialPersonnel: TrialPersonnelActions.ITrialPersonnelCreateActionCreator;
    validateTrialPersonnel: TrialPersonnelActions.ITrialPersonnelValidateActionCreator;
}

const reduxFormName: string = "reduxForms.trialPersonnel";

const TrialInstitutionPersonnelPage: FC<TrialInstitutionPersonnelPageProps> = ({
    form,
    formState,
    formProperties,
    trialPersonnel,
    trialInstitution,
    lookups,
    loadingTrialInstitution,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    validationFailures,
    permissions,
    loadingTrialPersonnel,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    savingTrialPersonnel,
    saveTrialPersonnelSuccess,
    saveTrialPersonnelFailure,
    creatingTrialPersonnel,
    validatingTrialPersonnel,
    clearTrialInstitution,
    loadTrialInstitution,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialPersonnel,
    loadTrialPersonnel,
    saveTrialPersonnel,
    createTrialPersonnel,
    validateTrialPersonnel
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { personnelId } = useParams<{ personnelId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);
    const personnelIdNumber = parseInt(personnelId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.TrialPersonnel = {} as Dtos.TrialPersonnel;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialPersonnel }, extraData)
        );
    };


    // Load Trial Personnel
    useEffect(() => {
        if (trialInstitutionIdNumber && personnelIdNumber && !trialPersonnel && !loadingTrialPersonnel && !loadingTrialInstitution) {
            setDrawerItems(
                TrialInstitutionPersonnelSubMenu(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    personnelIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionPersonnelDetailsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    personnelIdNumber
                )
            );

            loadTrialPersonnel(personnelIdNumber);
            loadTrialInstitution(trialInstitutionIdNumber);
        } else if (
            trialInstitutionIdNumber &&
            !personnelIdNumber &&
            !trialPersonnel
        ) {
            setBreadcrumbs(
                TrialInstitutionPersonnelCreateBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber
                )
            );
            setDrawerItems([]);
            if (!trialInstitution && !loadingTrialInstitution) {
                loadTrialInstitution(trialInstitutionIdNumber);
            }
            else if (trialInstitution && !creatingTrialPersonnel) {
                createTrialPersonnel(null, trialInstitution.institutionId, null);
            }
        } else if (trialPersonnel && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        trialInstitutionIdNumber,
        personnelIdNumber,
        trialIdNumber,
        trialPersonnel,
        loadingTrialPersonnel,
        loadingTrialInstitution,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        TrialInstitutionPersonnelSubMenu,
        TrialInstitutionPersonnelDetailsBreadcrumbs,
        TrialInstitutionPersonnelCreateBreadcrumbs,
        createTrialPersonnel,
        setFormInitalised,
        loadReduxForm,
        loadTrialInstitution,
        loadTrialPersonnel,
        permissions,
        trialInstitution,
        creatingTrialPersonnel
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
    }, [
        loadTrialInstitutionFailure,
        loadTrialPersonnelFailure,
        loadTrialInstitutionErrorCode,
        loadTrialPersonnelErrorCode,
        navigate,
    ]);

    // Update breadcrumbs & drawer items
    useEffect(() => {
        if (personnelIdNumber && trialPersonnel) {
            setBreadcrumbs(
                TrialInstitutionPersonnelDetailsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    personnelIdNumber,
                    trialPersonnel?.trial?.displayName,
                    trialPersonnel?.institution?.name,
                    trialPersonnel?.personnel?.displayWithSalutation
                )
            );
        } else if (!personnelIdNumber && trialInstitution) {
            setDrawerItems([]);
            setBreadcrumbs(
                TrialInstitutionPersonnelCreateBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitution?.trial?.displayName,
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        personnelIdNumber,
        trialPersonnel,
        trialInstitution,
        trialPersonnel,
        permissions,
        setBreadcrumbs,
        setDrawerItems,
        TrialInstitutionPersonnelDetailsBreadcrumbs,
        TrialInstitutionPersonnelCreateBreadcrumbs,
    ]);

    // Reload trial personnel after saving
    useEffect(() => {
        if (!savingTrialPersonnel && saveTrialPersonnelSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial institution personnel saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(
                    `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/personnel`
                );
            } else if (!personnelIdNumber) {
                navigate(
                    `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/personnel/${trialPersonnel?.id}/details`
                );
                clearTrialPersonnel();
                clearTrialInstitution();
            }
        }
    }, [
        savingTrialPersonnel,
        saveTrialPersonnelSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialPersonnel && saveTrialPersonnelFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialPersonnel, saveTrialPersonnelFailure]);

    // Cleanup effect to clear the personnel on component unmount
    useEffect(() => {
        return () => {
            clearTrialPersonnel();
            clearTrialInstitution();
            resetForm(reduxFormName);
        };
    }, [clearTrialPersonnel, clearTrialInstitution, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialPersonnel = {
                groupLevelTypeId: 1,
                trialId: trialIdNumber,
                institutionId: trialInstitution?.institutionId,
            } as Dtos.TrialPersonnel;

            await saveTrialPersonnel(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialPersonnel) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(
            `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/personnel`
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialPersonnel = {} as Dtos.TrialPersonnel;

            if (form && hasBeenSubmitted && !validatingTrialPersonnel) {
                await validateTrialPersonnel(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personnel 
                ? { label: form?.personnel?.displayWithSalutation, value: form?.personnelId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                personnelIdNumber,
                permissions,
                Dtos.Permission.TrialPersonnelRoleUpdate,
                Dtos.Permission.TrialPersonnelRoleCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialPersonnel ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            personnelDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Personnel Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            personnelId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value: form?.personnelId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personnel
                                                        ? [{ label: form.personnel.displayWithSalutation, value: form.personnel.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-lg-1",
                                                fieldClassName: "col-sm-12 col-lg-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            roleDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Role Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            roleId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "trialPersonnel",
                                                                "roleId",
                                                                lookups
                                                            ),
                                                            form.roleId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-lg-1",
                                                fieldClassName:
                                                    "col-sm-12 col-lg-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            dateStarted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            dateCeased: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.personnelId,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialPersonnel",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        personnelIdNumber,
        permissions,
        savingTrialPersonnel,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        personnelSearchLoader
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialPersonnelRoleView}
            loading={
                loadingTrialPersonnel ||
                savingTrialPersonnel ||
                creatingTrialPersonnel ||
                loadingTrialInstitution
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionPersonnelPageProps> => {
    let trialPersonnel: Dtos.TrialPersonnel | undefined = !(
        state.trialPersonnel.formData instanceof Array
    )
        ? state.trialPersonnel.formData
        : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined = !(
        state.trialInstitution.formData instanceof Array
    )
        ? state.trialInstitution.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialPersonnel: trialPersonnel!,
        trialInstitution: trialInstitution!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        savingTrialPersonnel:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Pending,
        saveTrialPersonnelSuccess:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Success,
        saveTrialPersonnelFailure:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Failure,
        creatingTrialPersonnel:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Pending,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode:
            state.trialInstitution.loadState.errorCode,
        validatingTrialPersonnel:
            state.trialPersonnel.validateState &&
            state.trialPersonnel.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.trialPersonnel.lookups,
        form: state.reduxForms.trialPersonnel,
        formState: state.trialPersonnel.formState,
        formProperties: state.trialPersonnel.formProperties,
        validationFailures: trialPersonnel
            ? state.trialPersonnel.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionPersonnelPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.LoadTrialPersonnelById,
            dispatch
        ),
        clearTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.Clear,
            dispatch
        ),
        saveTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.SaveTrialPersonnel,
            dispatch
        ),
        createTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.CreateTrialPersonnel,
            dispatch
        ),
        validateTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.ValidateTrialPersonnel,
            dispatch
        ),
        loadTrialInstitution: bindActionCreators(
            TrialInstitutionActions.LoadTrialInstitutionById,
            dispatch
        ),
        clearTrialInstitution: bindActionCreators(
            TrialInstitutionActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionPersonnelPage);
